import React, { useState, useEffect, useMemo, Component } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw,
  LayoutDashboard,
  AlertCircle,
  Loader2,
  Calendar,
  Hash,
  ChevronDown,
  ChevronRight,
  Search,
  Package,
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowRight,
  Eye,
  User,
  ClipboardList,
  FileText,
  Mail,
  Truck,
  Plus,
  ExternalLink,
  Menu,
  LogOut,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  TooltipProvider,
} from "../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";

import {
  UsersTab,
  RentalsTab,
  PoleRequestsTab,
  PolesTab,
  CheckInsOutsTab,
  TransactionsTab,
  SubscriptionCalendarTab,
  API,
  formatEST,
  dataCache,
  STATUS_ORDER,
  getStatusConfig,
  parseLengthForSort,
  parseWeightForSort,
} from "../components/dashboard";
import RentalDetailPage from "./RentalDetailPage";
import UserDetailPage from "./UserDetailPage";

// Error boundary to catch mobile rendering crashes
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <p className="text-red-600 font-bold mb-2">Something went wrong loading this tab</p>
          <pre className="text-xs text-left bg-red-50 p-3 rounded overflow-auto max-h-40 mb-4">
            {this.state.error?.message || 'Unknown error'}
            {'\n'}
            {this.state.error?.stack?.split('\n').slice(0, 5).join('\n')}
          </pre>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== KANBAN CARD COMPONENTS ====================

function PoleDetailsDialog({ pole, rental, open, onClose }) {
  if (!pole) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Pole Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500">Pole Code</label>
              <div className="font-mono font-semibold">{pole.pole_code || `#${pole.pole_id}`}</div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Status</label>
              <Badge variant={getStatusConfig(rental?.custom_status || 'Available').badge}>
                {rental?.custom_status || 'Available'}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500">Length</label>
              <div className="font-semibold text-lg">{pole.length || '-'}</div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Weight</label>
              <div className="font-semibold text-lg">{pole.weight || '-'}</div>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Brand</label>
            <div className="font-medium">{pole.brand || '-'}</div>
          </div>
          {rental?.renter_name && (
            <div className="border-t pt-4">
              <label className="text-xs text-slate-500">Current Renter</label>
              <div className="font-medium flex items-center gap-2">
                <User className="w-4 h-4" />{rental.renter_name}
              </div>
            </div>
          )}
          {rental?.rental_id && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Rental ID</label>
                <div className="font-mono">#{rental.rental_id}</div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Rental Period</label>
                <div className="text-sm">
                  {formatEST(rental.start_date, 'MMM d, yyyy')}
                  {rental.end_date && ` → ${formatEST(rental.end_date, 'MMM d, yyyy')}`}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PoleActions({ pole, rental, status, onViewDetails }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`pole-actions-${pole.pole_id}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => onViewDetails(pole, rental)}>
          <Eye className="mr-2 h-4 w-4" />View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => toast.info(`Edit: Pole ${pole.pole_code || pole.pole_id}`)}>
          <Edit className="mr-2 h-4 w-4" />Edit Pole
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {status === 'Available' ? (
          <DropdownMenuItem onClick={() => toast.info(`Create Rental: Pole ${pole.pole_code || pole.pole_id}`)}>
            <ArrowRight className="mr-2 h-4 w-4" />Create Rental
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem onClick={() => toast.info(`Change Status: Pole ${pole.pole_code || pole.pole_id}`)}>
              <ArrowRight className="mr-2 h-4 w-4" />Change Status
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => toast.info(`End Rental: Pole ${pole.pole_code || pole.pole_id}`)} className="text-red-600">
              <Trash2 className="mr-2 h-4 w-4" />End Rental
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PoleItem({ pole, rental, status, showActions = false, onViewDetails }) {
  const lengthWeight = [pole.length, pole.weight].filter(Boolean).join(' - ');
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        {lengthWeight && <div className="font-semibold text-slate-900 text-sm mb-1">{lengthWeight}</div>}
        {pole.brand && <div className="text-xs text-slate-600 mb-1">{pole.brand}</div>}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Package className="w-3 h-3" />
          <span className="font-mono">{pole.pole_code || `Pole #${pole.pole_id}`}</span>
        </div>
      </div>
      {showActions && <PoleActions pole={pole} rental={rental} status={status} onViewDetails={onViewDetails} />}
    </div>
  );
}

function RentalCard({ rental, poles, config, status, onViewDetails, onUserClick, onRentalClick }) {
  const [expanded, setExpanded] = useState(false);
  const firstPole = poles[0];

  const handleUserClick = (e) => {
    e.stopPropagation();
    if (rental.user_id && onUserClick) {
      if (e.metaKey || e.ctrlKey) {
        window.open(`/users/${rental.user_id}`, '_blank');
      } else {
        onUserClick(rental.user_id);
      }
    }
  };

  const handleRentalClick = (e) => {
    e.stopPropagation();
    if (rental.rental_id && onRentalClick) {
      if (e.metaKey || e.ctrlKey) {
        window.open(`/rentals/${rental.rental_id}`, '_blank');
      } else {
        onRentalClick(rental.rental_id);
      }
    }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid={`rental-card-${rental.rental_id || `pole-${firstPole.pole_id}`}`}>
      <div className="p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="text-slate-400">{expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
            <div>
              {rental.renter_name && (
                <div 
                  className={`flex items-center gap-2 mb-1 ${rental.user_id ? 'cursor-pointer hover:text-blue-600' : ''}`}
                  onClick={handleUserClick}
                  data-testid={`rental-user-link-${rental.rental_id}`}
                >
                  <User className="w-4 h-4 text-slate-500" />
                  <span className={`font-bold text-slate-900 text-base ${rental.user_id ? 'hover:text-blue-600 hover:underline' : ''}`}>{rental.renter_name}</span>
                </div>
              )}
              <div className="text-sm text-slate-600">{poles.length} pole{poles.length > 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>
        <div className="space-y-1 text-xs text-slate-500">
          {rental.rental_id && (
            <div 
              className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
              onClick={handleRentalClick}
              data-testid={`rental-link-${rental.rental_id}`}
            >
              <Hash className="w-3 h-3" />
              <span className="font-mono hover:underline">Rental #{rental.rental_id}</span>
              <ExternalLink className="w-3 h-3" />
            </div>
          )}
          {rental.start_date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              <span className="font-mono">{formatEST(rental.start_date, 'MMM d, yyyy')}{rental.end_date && ` → ${formatEST(rental.end_date, 'MMM d, yyyy')}`}</span>
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          {poles.map((pole, idx) => (
            <div key={`${pole.pole_id}-${idx}`} className="p-3 border-b border-slate-100 last:border-b-0">
              <PoleItem pole={pole} rental={rental} status={status} showActions={true} onViewDetails={onViewDetails} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AvailablePoleCard({ pole, config, onViewDetails, status = "Available" }) {
  const [expanded, setExpanded] = useState(false);
  const lengthWeight = [pole.length, pole.weight].filter(Boolean).join(' - ');

  return (
    <div className="bg-white rounded-md border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid={`available-pole-${pole.pole_id}`}>
      <div className="p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="text-slate-400">{expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
            <div>
              {lengthWeight && <div className="font-bold text-slate-900 text-base mb-1">{lengthWeight}</div>}
              {pole.brand && <div className="text-sm text-slate-600 mb-1">{pole.brand}</div>}
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Package className="w-3 h-3" /><span className="font-mono">{pole.pole_code || `Pole #${pole.pole_id}`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-3">
          <PoleItem pole={pole} rental={null} status={status} showActions={true} onViewDetails={onViewDetails} />
        </div>
      )}
    </div>
  );
}

// ==================== MAIN DASHBOARD COMPONENT ====================

export default function KanbanDashboard({ defaultTab = "kanban", createMode: initialCreateMode = false, rentalId: rentalIdProp = null, userId: userIdProp = null }) {
  const navigate = useNavigate();
  const params = useParams();
  const rentalId = rentalIdProp === "fromUrl" ? params.rentalId : rentalIdProp;
  const userId = userIdProp === "fromUrl" ? params.userId : userIdProp;
  const [ganttData, setGanttData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [lengthFilter, setLengthFilter] = useState("all");
  const [weightFilter, setWeightFilter] = useState("all");
  const [tableOpen, setTableOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedPole, setSelectedPole] = useState(null);
  const [selectedRental, setSelectedRental] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dbInfo, setDbInfo] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Sync activeTab with defaultTab when route changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);
  
  const handleTabChange = (newTab) => {
    const routes = {
      'kanban': '/kanban',
      'rentals': '/rentals',
      'requests': '/requests',
      'poles': '/poles',
      'checkins': '/checkins',
      'users': '/users',
      'transactions': '/transactions',
      'subscription-calendar': '/subscription-calendar'
    };
    navigate(routes[newTab] || '/kanban');
  };
  
  const openUserDetails = (userId, userName) => {
    if (userId) {
      navigate(`/users/${userId}`);
    }
  };
  
  const checkinsCreateMode = initialCreateMode;

  const handleViewDetails = (pole, rental) => {
    setSelectedPole(pole);
    setSelectedRental(rental);
    setDetailsOpen(true);
  };

  const fetchDbInfo = async () => {
    try {
      const response = await axios.get(`${API}/db-info`);
      setDbInfo(response.data);
    } catch (err) {
      console.error("Failed to fetch DB info");
    }
  };

  const fetchData = async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = dataCache.get('kanban_gantt');
      if (cached) {
        setGanttData(cached);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/poles/gantt`);
      setGanttData(response.data);
      dataCache.set('kanban_gantt', response.data);
      if (forceRefresh) {
        toast.success("Data refreshed");
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchData(); 
    fetchDbInfo();
  }, []);

  const kanbanColumns = useMemo(() => {
    if (!ganttData) return {};
    const columns = {};
    STATUS_ORDER.forEach(status => { columns[status] = { rentals: {}, available: [] }; });
    
    (ganttData.rentals || []).forEach(rental => {
      const status = rental.custom_status || 'Available';
      if (!columns[status]) columns[status] = { rentals: {}, available: [] };
      if (lengthFilter !== "all" && rental.length !== lengthFilter) return;
      if (weightFilter !== "all" && rental.weight !== weightFilter) return;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = [rental.pole_code, rental.length, rental.weight, rental.brand, rental.renter_name, String(rental.rental_id || ''), String(rental.pole_id), status]
          .some(field => (field || '').toLowerCase().includes(searchLower));
        if (!matchesSearch) return;
      }
      if (!rental.rental_id) {
        columns[status].available.push({ pole_id: rental.pole_id, pole_code: rental.pole_code, length: rental.length, weight: rental.weight, brand: rental.brand });
        return;
      }
      const rentalId = rental.rental_id;
      if (!columns[status].rentals[rentalId]) {
        columns[status].rentals[rentalId] = { rental_id: rentalId, user_id: rental.user_id, start_date: rental.start_date, end_date: rental.end_date, custom_status: status, renter_name: rental.renter_name, poles: [] };
      }
      columns[status].rentals[rentalId].poles.push({ pole_id: rental.pole_id, pole_code: rental.pole_code, length: rental.length, weight: rental.weight, brand: rental.brand });
    });
    
    const result = {};
    Object.keys(columns).forEach(status => {
      const rentalsArray = Object.values(columns[status].rentals);
      let availableArray = columns[status].available;
      availableArray.sort((a, b) => {
        const lengthDiff = parseLengthForSort(a.length) - parseLengthForSort(b.length);
        return lengthDiff !== 0 ? lengthDiff : parseWeightForSort(a.weight) - parseWeightForSort(b.weight);
      });
      result[status] = { rentals: rentalsArray, available: availableArray, total: rentalsArray.length + availableArray.length };
    });
    return result;
  }, [ganttData, searchTerm, lengthFilter, weightFilter]);

  const allRentals = useMemo(() => ganttData?.rentals || [], [ganttData]);
  const totalItems = useMemo(() => Object.values(kanbanColumns).reduce((sum, col) => sum + (col.total || 0), 0), [kanbanColumns]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-center"><Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto mb-4" /><p className="text-slate-600 font-medium">Loading data...</p></div></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white border border-red-200 rounded-lg p-8 max-w-lg text-center shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Failed to Load Data</h2>
          <p className="text-slate-600 mb-4 font-mono text-sm">{error}</p>
          <Button onClick={() => fetchData(true)} data-testid="retry-button"><RefreshCw className="w-4 h-4 mr-2" />Retry</Button>
        </div>
      </div>
    );
  }

  const columnOrder = STATUS_ORDER.filter(s => (kanbanColumns[s]?.total || 0) > 0);

  const handleMobileNavClick = (tab) => {
    handleTabChange(tab);
    setMobileMenuOpen(false);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50" data-testid="kanban-dashboard">
        <PoleDetailsDialog pole={selectedPole} rental={selectedRental} open={detailsOpen} onClose={() => setDetailsOpen(false)} />

        <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-[1920px] mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Mobile Menu Button */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm" className="sm:hidden h-8 w-8 p-0">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[280px] p-0">
                    <SheetHeader className="p-4 border-b">
                      <SheetTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                          <LayoutDashboard className="w-4 h-4 text-white" />
                        </div>
                        <span>Pole Rental</span>
                        {dbInfo?.using_staging && (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-400 bg-amber-50">
                            STAGING
                          </Badge>
                        )}
                      </SheetTitle>
                    </SheetHeader>
                    <nav className="flex flex-col p-2">
                      {[
                        { tab: 'kanban', icon: LayoutDashboard, label: 'Kanban Board' },
                        { tab: 'rentals', icon: FileText, label: 'Rentals' },
                        { tab: 'requests', icon: ClipboardList, label: 'Pole Requests' },
                        { tab: 'poles', icon: Package, label: 'Poles Inventory' },
                        { tab: 'checkins', icon: Truck, label: 'Check-ins/Outs' },
                        { tab: 'users', icon: User, label: 'Users' },
                        { tab: 'transactions', icon: DollarSign, label: 'Transactions' },
                        { tab: 'subscription-calendar', icon: Calendar, label: 'Sub Calendar' },
                      ].map(({ tab, icon: Icon, label }) => (
                        <button
                          key={tab}
                          onClick={() => handleMobileNavClick(tab)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                          <Icon className="w-5 h-5" />
                          <span>{label}</span>
                        </button>
                      ))}
                      <div className="border-t my-2" />
                      <button
                        onClick={() => {
                          localStorage.removeItem('pole_dashboard_token');
                          localStorage.removeItem('pole_dashboard_user');
                          window.location.href = '/';
                        }}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                      </button>
                    </nav>
                  </SheetContent>
                </Sheet>
                
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-900 rounded-sm flex items-center justify-center hidden sm:flex">
                  <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Pole Rental</h1>
                  <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">Inventory Management</p>
                </div>
                {dbInfo?.using_staging && (
                  <Badge variant="outline" className="ml-1 sm:ml-2 text-[10px] sm:text-xs text-amber-600 border-amber-400 bg-amber-50 px-1.5 py-0.5 hidden sm:inline-flex">
                    STAGING
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-4">
                <Button variant="outline" size="sm" onClick={() => fetchData(true)} className="h-8 px-2 sm:px-3" data-testid="refresh-btn">
                  <RefreshCw className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Desktop Tab Navigation */}
        <div className="bg-white border-b border-slate-200 sticky top-14 sm:top-16 z-10 hidden sm:block">
          <div className="max-w-[1920px] mx-auto">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="h-11 sm:h-12 w-full justify-start overflow-x-auto scrollbar-hide px-2 sm:px-6 lg:px-8 gap-1 sm:gap-2 flex-nowrap">
                {[
                  { value: 'kanban', icon: LayoutDashboard, label: 'Kanban' },
                  { value: 'rentals', icon: FileText, label: 'Rentals' },
                  { value: 'requests', icon: ClipboardList, label: 'Requests' },
                  { value: 'poles', icon: Package, label: 'Poles' },
                  { value: 'checkins', icon: Truck, label: 'Check-ins/Outs' },
                  { value: 'users', icon: User, label: 'Users' },
                  { value: 'transactions', icon: DollarSign, label: 'Transactions' },
                  { value: 'subscription-calendar', icon: Calendar, label: 'Sub Calendar' },
                ].map(({ value, icon: Icon, label }) => (
                  <a key={value} href={`/kanban?tab=${value}`} onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); handleTabChange(value); }}} className="contents">
                    <TabsTrigger value={value} className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap flex-shrink-0" data-testid={`tab-${value}`}>
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </TabsTrigger>
                  </a>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>

        <main className="max-w-[1920px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          {rentalId ? (
            <div className="[&>div]:min-h-0 [&>div]:bg-transparent">
              <RentalDetailPage />
            </div>
          ) : userId ? (
            <div className="[&>div]:min-h-0 [&>div]:bg-transparent">
              <UserDetailPage />
            </div>
          ) : activeTab === "kanban" ? (
            <>
              {/* Filters for Kanban Board */}
              <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6 flex-wrap">
                <div className="relative flex-1 min-w-[120px] max-w-[200px]">
                  <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 sm:pl-9 w-full font-mono text-sm h-9" data-testid="search-input" />
                </div>
                <Select value={lengthFilter} onValueChange={setLengthFilter}>
                  <SelectTrigger className="w-[100px] sm:w-[130px] h-9 text-xs sm:text-sm" data-testid="length-filter"><SelectValue placeholder="Length" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Lengths</SelectItem>
                    {(ganttData?.lengths || []).map(len => <SelectItem key={len} value={len}>{len}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={weightFilter} onValueChange={setWeightFilter}>
                  <SelectTrigger className="w-[100px] sm:w-[130px] h-9 text-xs sm:text-sm" data-testid="weight-filter"><SelectValue placeholder="Weight" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Weights</SelectItem>
                    {(ganttData?.weights || []).map(wt => <SelectItem key={wt} value={wt}>{wt}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 sm:gap-2 ml-auto">
                  <span className="text-xs sm:text-sm text-slate-500">Total:</span>
                  <span className="font-mono text-xs sm:text-sm font-medium text-slate-900" data-testid="total-items">{totalItems}</span>
                </div>
              </div>
              
              {/* Status legend */}
              <div className="flex items-center gap-3 sm:gap-6 flex-nowrap overflow-x-auto pb-2 mb-4 sm:mb-6 scrollbar-hide">
                <div className="flex items-center gap-2 sm:gap-4 ml-auto flex-nowrap">
                  {columnOrder.map(status => {
                    const config = getStatusConfig(status);
                    return (
                      <div key={status} className="flex items-center gap-1 sm:gap-2 whitespace-nowrap flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full ${config.dot}`} />
                        <span className="text-[10px] sm:text-xs text-slate-600">{status}</span>
                        <span className="font-mono text-[10px] sm:text-xs font-semibold text-slate-900">{kanbanColumns[status]?.total || 0}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
                {columnOrder.map(status => {
                  const config = getStatusConfig(status);
                  const column = kanbanColumns[status];
                  return (
                    <div key={status} className={`flex-shrink-0 w-[340px] rounded-lg border ${config.border} ${config.bg}`} data-testid={`kanban-column-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className={`px-4 py-3 ${config.headerBg} rounded-t-lg border-b ${config.border}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${config.dot}`} />
                            <h3 className={`font-semibold text-sm ${config.text}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{status}</h3>
                          </div>
                          <Badge variant={config.badge} className="font-mono text-xs">{column?.total || 0}</Badge>
                        </div>
                      </div>
                      <ScrollArea className="h-[calc(100vh-320px)]">
                        <div className="p-3 space-y-3">
                          {(column?.rentals?.length === 0 && column?.available?.length === 0) ? (
                            <div className="text-center py-8 text-slate-400 text-sm">No items</div>
                          ) : (
                            <>
                              {column?.rentals?.map((rental, idx) => (
                                <RentalCard key={`rental-${rental.rental_id}-${idx}`} rental={rental} poles={rental.poles} config={config} status={status} onViewDetails={handleViewDetails} onUserClick={(userId) => navigate(`/users/${userId}`)} onRentalClick={(rentalId) => navigate(`/rentals/${rentalId}`)} />
                              ))}
                              {column?.available?.map((pole, idx) => (
                                <AvailablePoleCard key={`available-${pole.pole_id}-${idx}`} pole={pole} config={config} onViewDetails={handleViewDetails} status={status} />
                              ))}
                            </>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  );
                })}
              </div>

              <Collapsible open={tableOpen} onOpenChange={setTableOpen} className="mt-6">
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors" data-testid="table-toggle">
                      <span className="font-medium text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Detailed Data Table</span>
                      <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${tableOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ScrollArea className="max-h-[400px]">
                      <Table data-testid="data-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="font-mono">Renter</TableHead>
                            <TableHead className="font-mono">Rental ID</TableHead>
                            <TableHead className="font-mono">Status</TableHead>
                            <TableHead className="font-mono">Length</TableHead>
                            <TableHead className="font-mono">Weight</TableHead>
                            <TableHead className="font-mono">Pole Code</TableHead>
                            <TableHead className="font-mono">Brand</TableHead>
                            <TableHead className="font-mono">Start</TableHead>
                            <TableHead className="font-mono">Return</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allRentals.map((rental, idx) => {
                            const config = getStatusConfig(rental.custom_status);
                            return (
                              <TableRow 
                                key={`${rental.pole_id}-${rental.rental_id}-${idx}`}
                                className={rental.rental_id ? 'cursor-pointer hover:bg-slate-50' : ''}
                                onClick={() => rental.rental_id && navigate(`/rentals/${rental.rental_id}`)}
                              >
                                <TableCell 
                                  className={`font-medium ${rental.user_id ? 'cursor-pointer hover:text-blue-600' : ''}`}
                                  onClick={(e) => {
                                    if (rental.user_id) {
                                      e.stopPropagation();
                                      navigate(`/users/${rental.user_id}`);
                                    }
                                  }}
                                >
                                  {rental.renter_name || '-'}
                                </TableCell>
                                <TableCell className="font-mono">{rental.rental_id ? `#${rental.rental_id}` : '-'}</TableCell>
                                <TableCell><Badge variant={config.badge}>{rental.custom_status || 'Available'}</Badge></TableCell>
                                <TableCell className="font-mono">{rental.length || '-'}</TableCell>
                                <TableCell className="font-mono">{rental.weight || '-'}</TableCell>
                                <TableCell className="font-mono">{rental.pole_code || `#${rental.pole_id}`}</TableCell>
                                <TableCell>{rental.brand || '-'}</TableCell>
                                <TableCell className="font-mono text-sm">{formatEST(rental.start_date, 'MMM d, yyyy')}</TableCell>
                                <TableCell className="font-mono text-sm">{formatEST(rental.end_date, 'MMM d, yyyy')}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </>
          ) : activeTab === "rentals" ? (
            <RentalsTab onUserClick={openUserDetails} />
          ) : activeTab === "requests" ? (
            <TabErrorBoundary key="requests">
              <PoleRequestsTab onUserClick={openUserDetails} />
            </TabErrorBoundary>
          ) : activeTab === "poles" ? (
            <TabErrorBoundary key="poles">
              <PolesTab onUserClick={openUserDetails} />
            </TabErrorBoundary>
          ) : activeTab === "users" ? (
            <TabErrorBoundary key="users">
              <UsersTab onUserClick={openUserDetails} />
            </TabErrorBoundary>
          ) : activeTab === "transactions" ? (
            <TransactionsTab />
          ) : activeTab === "subscription-calendar" ? (
            <SubscriptionCalendarTab />
          ) : (
            <CheckInsOutsTab initialCreateMode={checkinsCreateMode} onUserClick={openUserDetails} />
          )}
        </main>
      </div>
    </TooltipProvider>
  );
}
